<script lang="ts">
  // BJL-CHECK: Card, Tag, Badge — none used, and this file declares no interactive element of
  // its own (the ✕ it now HOSTS arrives as a snippet, already a bjl `Button`, from
  // `RidingPanel`). Card would frame it in the app's own `#181d27` chrome, which is the one
  // surface a depiction of a lit sign must not wear; the band has no surface of its own by
  // design. RouteBadge is the app's own bullet and is reused as-is.
  // See artifacts/deviations/RiderPanel.md.
  //
  // The sign band: the top row of the boarded-train panel, above the rollsign strip. Bullet,
  // then the service name over where the train is going, then the one line with a deadline.
  //
  // It is a ROW and not the LCD column it was. An R160 car front carries two displays side by
  // side and the column reproduced that, but beside the strip it could only ever have
  // 104–148px — too narrow to be the landscape display it was imitating, and every px of it
  // came out of the strip, whose diagonal callout is what actually has to fit. Full width, the
  // same three marks need one band and hand the strip 112px back. The reasoning and what that
  // bought are at `STRIP_MIN_W` in `shell.ts`.
  //
  // Set in `LCD_FONT`, i.e. Helvetica, NOT the strip's dot-matrix `LED_FONT`. That is not an
  // inconsistency: an R160 front carries two displays, and only the rollsign is dot matrix.
  import type { Snippet } from 'svelte'
  import { getRouteLongName } from '../../ui/colors'
  import { LCD_FONT, LCD_GREEN, LCD_INK, LCD_RED, LCD_SIGN_NAME } from '../../ui/theme'
  import { SIGN_ROW_BULLET_PX, SIGN_ROW_DEST_PX, SIGN_ROW_NAME_PX } from './shell'
  import RouteBadge from '../RouteBadge.svelte'

  interface Props {
    routeId: string
    /** Where the train is going — the last stop it still calls at. */
    terminus: string | null
    /** The one line with a deadline: the alight countdown, or the wrong-vehicle warning. */
    notice: string | null
    /** A notice the rider has to act on THIS stop is red; anything else is calm green. */
    noticeUrgent: boolean
    /**
     * The band's far right end — the panel's ✕, in practice. A snippet rather than a callback
     * prop for the same reason `BusStopScreen`'s footer takes one: this component owns where
     * the band's marks sit, `RidingPanel` owns what the control IS and what it does.
     *
     * It is here rather than a row of its own because the ROUTE line is the line a rider is
     * looking at, and because the band already costs 30px for the bullet — a 28px button is
     * free height. It renders OUTSIDE the sign's own paint (see the markup).
     */
    trailing?: Snippet
  }

  const { routeId, terminus, notice, noticeUrgent, trailing }: Props = $props()

  const serviceName = $derived(getRouteLongName(routeId))
</script>

<div
  style="
    flex-shrink: 0; display: flex; align-items: center; gap: 10px;
    font-family: {LCD_FONT}; overflow: hidden;
  "
>
  <RouteBadge {routeId} size={SIGN_ROW_BULLET_PX} />

  <!-- Service name over destination, hugging each other exactly as they do on the real
       display: no eyebrow, no "TO", nothing between them. Both stay on one line — at this
       width the longest service name in the table is 132px and the longest station name on
       the system is 224, so the ellipsis is a backstop and not a mechanism. -->
  <div style="flex: 1; min-width: 0; display: flex; flex-direction: column;">
    {#if serviceName}
      <div
        style="
          color: {LCD_SIGN_NAME}; font-size: {SIGN_ROW_NAME_PX}px; line-height: 1.15;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        "
      >{serviceName}</div>
    {/if}
    <!-- The real display carries a routing note on a second line here ("via 59 St"); the feed
         gives us no via, so ours is the terminus alone. Inventing that line is the one thing
         that would make this stop matching the display. -->
    <div
      style="
        color: {LCD_INK}; font-size: {SIGN_ROW_DEST_PX}px; line-height: 1.2;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      "
    >{terminus ?? routeId}</div>
  </div>

  <!-- The band's right end, which is empty until there is something to say. Full width is
       what makes that affordable: in the column this shared a 104px measure with everything
       else and had to wrap. The 45% is measured against the row, so the ✕ beyond it does not
       change what a notice may take — it takes it from the destination instead, which is the
       block that has ellipsis to spare. -->
  {#if notice}
    <div
      style="
        flex: none; max-width: 45%; text-align: right;
        color: {noticeUrgent ? LCD_RED : LCD_GREEN};
        font-size: 11px; line-height: 1.2; letter-spacing: 0.02em;
      "
    >{notice}</div>
  {/if}

  <!-- Past the notice, and deliberately last: the ✕ is APP chrome sitting at the end of a
       depiction of a lit sign, so it goes outside everything the sign paints rather than
       between two of its marks. Nothing is wrapped around it — the button carries its own
       box (`RAIL_ICON_BTN`), and a container with padding here would push the notice in. -->
  {#if trailing}
    {@render trailing()}
  {/if}
</div>
